import { useEffect, useState } from 'react'
import * as AppPreferencesClient from 'api/clients/AppPreferencesClient'
import { ReactComponent as MinusIcon } from 'assets/icons/minus.svg'
import { Spinner } from 'common/Spinner/Spinner'
import { Checkbox } from 'components/Form/Checkbox/Checkbox'
import SettingsStyles from 'components/Settings/SettingsModal.module.scss'
import { Cell } from 'components/Table/components/Cell/Cell'
import { Header } from 'components/Table/components/Header/Header'
import { Headers } from 'components/Table/components/Headers/Headers'
import { Row } from 'components/Table/components/Row/Row'
import { Table } from 'components/Table/Table'
import useUserPreferences from 'hooks/useUserPreferences'
import { NotificationChannel, NotificationGroup, NotificationSettings, Workspace } from 'types'

import Styles from './Notifications.module.scss'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { useIsBlaze } from 'hooks/useIsBlaze'
import { Heading } from 'blaze-ui/Heading/Heading'
import { Text } from 'blaze-ui/Text/Text'
import { useFlipperFlag } from 'hooks/useFlipperFlag'
import { Modal } from 'almanac-ui/Modal'

type SettingMetadataItem = {
  heading: string
  description: string
  availableChannels: NotificationChannel[]
}
type SettingMetadata = {
  [key in NotificationGroup]: SettingMetadataItem
}
export const SETTINGS_METADATA: SettingMetadata = {
  documentAccess: {
    heading: 'Document access',
    description: 'Notifications when someone wants access or when someones shares a document with you.',
    availableChannels: ['inbox', 'email', 'slack'],
  },
  reviews: {
    heading: 'Reviews',
    description: 'Notifications when someone requests a review or approval.',
    availableChannels: ['inbox', 'email', 'slack'],
  },
  comments: {
    heading: 'Comments',
    description: 'Notification when someone comments on your documents or mentions you in a comment.',
    availableChannels: ['inbox', 'email', 'slack'],
  },
  tasks: {
    heading: 'Tasks',
    description: 'Notification when tasks are created on your document or assigned to you.',
    availableChannels: ['inbox', 'email', 'slack'],
  },
  reminders: {
    heading: 'Reminders',
    description: 'Notification when tasks or reviews due dates are approaching.',
    availableChannels: ['email'],
  },
  scheduledPostsPublished: {
    heading: 'Scheduled Posts Published',
    description: 'Notification when a scheduled post is successfully published to an external platform.',
    availableChannels: ['email'],
  },
  scheduledPostsFailedToPublish: {
    heading: 'Scheduled Posts Failed to Publish',
    description: 'Notification when a scheduled post fails to publish to an external platform.',
    availableChannels: ['email'],
  },
}
interface Props {
  workspace: Workspace
}
export const Notifications = ({ workspace }: Props) => {
  const isSettingsModalUpdateEnabled = useFlipperFlag('settings_modal_update')
  const { isMobile } = useResponsiveDesign()
  const isBlaze = useIsBlaze()
  const {
    data: userPreferences,
    isLoading: userPreferencesLoading,
    refetch: refetchUserPreferences,
  } = useUserPreferences(workspace?.id)
  const [settings, setSettings] = useState<NotificationSettings>(userPreferences?.notifications as NotificationSettings)
  async function updateSettings(newSettings: NotificationSettings) {
    setSettings(newSettings)
    await AppPreferencesClient.updateUserAppPreferences({ notifications: newSettings })
    refetchUserPreferences()
  }
  function onCellCheck(group: NotificationGroup, channel: NotificationChannel, isSelected: boolean) {
    const newSettings = {
      ...settings,
      [group]: {
        ...settings[group],
        [channel]: isSelected,
      },
    }
    updateSettings(newSettings)
  }
  function onColumnCheck(channel: NotificationChannel, isSelected: boolean) {
    const newSettings = Object.keys(SETTINGS_METADATA).reduce(
      (prev, curr) => ({ ...prev, [curr]: { ...settings[curr as NotificationGroup], [channel]: isSelected } }),
      {},
    )
    updateSettings(newSettings)
  }
  function markedCheckboxesByColumn(channel: NotificationChannel) {
    const allGroups = Object.entries(SETTINGS_METADATA).filter(([_, data]) => data.availableChannels.includes(channel))
    const enabledGroups = allGroups.filter(([key, _]) => settings?.[key as NotificationGroup]?.[channel])
    switch (enabledGroups.length / allGroups.length) {
      case 1:
        return 'all'
      case 0:
        return 'none'
      default:
        return 'some'
    }
  }

  const markedCheckboxesByColumns = {
    inbox: markedCheckboxesByColumn('inbox'),
    email: markedCheckboxesByColumn('email'),
    slack: markedCheckboxesByColumn('slack'),
  }
  useEffect(() => {
    setSettings(userPreferences?.notifications as NotificationSettings)
  }, [userPreferences])
  return (
    <>
      {!isMobile && !isSettingsModalUpdateEnabled && (
        <header className={SettingsStyles.subHeader}>
          <Heading level={3}>Notifications {userPreferencesLoading && <Spinner />}</Heading>
        </header>
      )}
      {!isMobile && isSettingsModalUpdateEnabled && (
        <Modal.Header className={SettingsStyles.newHeader}>
          <Heading level={3}>Notifications {userPreferencesLoading && <Spinner />}</Heading>
        </Modal.Header>
      )}
      {!userPreferencesLoading && settings && (
        <Table className={Styles.table}>
          <Headers className={Styles.headerContainer}>
            {!isMobile && <Header width={500} className={Styles.header} />}
            <Header width={isMobile ? undefined : 100} className={Styles.header}>
              <Checkbox
                size={isMobile ? 'lg' : 'md'}
                variant="primary"
                isSelected={markedCheckboxesByColumns.inbox !== 'none'}
                checkIcon={markedCheckboxesByColumns.inbox === 'some' ? <MinusIcon /> : undefined}
                onChange={(isSelected) => onColumnCheck('inbox', isSelected)}
                aria-label="All inbox notifications"
                className={Styles.checkbox}
              />
              <span className={Styles.headerLabel}>Notifications</span>
            </Header>
            <Header width={isMobile ? undefined : 100} className={Styles.header}>
              <Checkbox
                size={isMobile ? 'lg' : 'md'}
                variant="primary"
                isSelected={markedCheckboxesByColumns.email !== 'none'}
                checkIcon={markedCheckboxesByColumns.email === 'some' ? <MinusIcon /> : undefined}
                onChange={(isSelected) => onColumnCheck('email', isSelected)}
                aria-label="All email notifications"
                className={Styles.checkbox}
              />
              <span className={Styles.headerLabel}>Email</span>
            </Header>
            {!isBlaze && (
              <Header width={isMobile ? undefined : 100} className={Styles.header}>
                <Checkbox
                  size={isMobile ? 'lg' : 'md'}
                  variant="primary"
                  isSelected={markedCheckboxesByColumns.slack !== 'none'}
                  checkIcon={markedCheckboxesByColumns.slack === 'some' ? <MinusIcon /> : undefined}
                  onChange={(isSelected) => onColumnCheck('slack', isSelected)}
                  aria-label="All slack notifications"
                  className={Styles.checkbox}
                />
                <span className={Styles.headerLabel}>Slack</span>
              </Header>
            )}
          </Headers>
          {Object.entries(SETTINGS_METADATA).map(([key, data]) => (
            <Row size="lg" key={key} className={Styles.row}>
              {isMobile && (
                <>
                  <Cell className={Styles.labelCell}>
                    <Heading level={5}>{data.heading}</Heading>
                    <Text variant="secondary" color="var(--dark-60)">
                      {data.description}
                    </Text>
                  </Cell>
                  <div className={Styles.checkboxes}>
                    <Cell className={Styles.cell}>
                      {data.availableChannels.includes('inbox') ? (
                        <>
                          <span>Notifications</span>
                          <Checkbox
                            size="lg"
                            variant="primary"
                            className={Styles.checkbox}
                            isSelected={settings[key as NotificationGroup]?.inbox}
                            onChange={(isSelected) => onCellCheck(key as NotificationGroup, 'inbox', isSelected)}
                            aria-label={`Inbox notifications for ${data.heading}`}
                          />
                        </>
                      ) : null}
                    </Cell>
                    <Cell className={Styles.cell}>
                      {data.availableChannels.includes('email') ? (
                        <>
                          <span>Email</span>
                          <Checkbox
                            size="lg"
                            variant="primary"
                            className={Styles.checkbox}
                            isSelected={settings[key as NotificationGroup]?.email}
                            onChange={(isSelected) => onCellCheck(key as NotificationGroup, 'email', isSelected)}
                            aria-label={`Email notifications for ${data.heading}`}
                          />
                        </>
                      ) : null}
                    </Cell>
                    {!isBlaze && (
                      <Cell className={Styles.cell}>
                        {data.availableChannels.includes('slack') ? (
                          <>
                            <span>Slack</span>
                            <Checkbox
                              size="lg"
                              variant="primary"
                              className={Styles.checkbox}
                              isSelected={settings[key as NotificationGroup]?.slack}
                              onChange={(isSelected) => onCellCheck(key as NotificationGroup, 'slack', isSelected)}
                              aria-label={`Slack notifications for ${data.heading}`}
                            />
                          </>
                        ) : null}
                      </Cell>
                    )}
                  </div>
                </>
              )}
              {!isMobile && (
                <>
                  <Cell width={500} className={Styles.labelCell}>
                    <Heading level={5}>{data.heading}</Heading>
                    <Text variant="secondary" color="var(--dark-60)">
                      {data.description}
                    </Text>
                  </Cell>
                  <Cell width={100} className={Styles.cell}>
                    {data.availableChannels.includes('inbox') ? (
                      <Checkbox
                        size="lg"
                        variant="primary"
                        className={Styles.checkbox}
                        isSelected={settings[key as NotificationGroup]?.inbox}
                        onChange={(isSelected) => onCellCheck(key as NotificationGroup, 'inbox', isSelected)}
                        aria-label={`Inbox notifications for ${data.heading}`}
                      />
                    ) : null}
                  </Cell>
                  <Cell width={100} className={Styles.cell}>
                    {data.availableChannels.includes('email') ? (
                      <Checkbox
                        size="lg"
                        variant="primary"
                        className={Styles.checkbox}
                        isSelected={settings[key as NotificationGroup]?.email}
                        onChange={(isSelected) => onCellCheck(key as NotificationGroup, 'email', isSelected)}
                        aria-label={`Email notifications for ${data.heading}`}
                      />
                    ) : null}
                  </Cell>
                  {!isBlaze && (
                    <Cell width={100} className={Styles.cell}>
                      {data.availableChannels.includes('slack') ? (
                        <Checkbox
                          size="lg"
                          variant="primary"
                          className={Styles.checkbox}
                          isSelected={settings[key as NotificationGroup]?.slack}
                          onChange={(isSelected) => onCellCheck(key as NotificationGroup, 'slack', isSelected)}
                          aria-label={`Slack notifications for ${data.heading}`}
                        />
                      ) : null}
                    </Cell>
                  )}
                </>
              )}
            </Row>
          ))}
        </Table>
      )}
    </>
  )
}
